import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import CreatorFrame from '../../../shared/CreatorFrame'
import { marketingApiService } from '../../services/marketingApiService'
import { marketingAction } from '../../../../_actions/marketing.actions'
import { ScheduleModal } from '../email-composer/components'
import { analyseSms, analyseSmsExpanded, SMS_MERGE_TAGS, previewSubstitute, MAX_SEGMENTS, hasOptOut as offersOptOut, withOptOut, OPT_OUT_LINE } from './smsUtils'
import { loadBusinessDetails, saveBusinessDetails, isBusinessDetailsComplete, hydrateBusinessDetails } from '../../../marketing/businessDetails'
import BusinessDetailsForm from '../../../marketing/BusinessDetailsForm'
import ClientPicker from '../../components/ClientPicker'
import SmsWalletTopupModal from './SmsWalletTopupModal'
import { formatUnitPence, walletBalancePence, messagePrices } from '../../../pricing/money'
import { useSendSites, SendLocationsCard, SiteResultsList, broadcastToSites } from '../../components/SendLocations'
import { CAMPAIGNS_VISIBLE, DISCOUNTS_VISIBLE } from '../../../../config/featureFlags'
import { SMS_IDEAS } from './smsIdeas'
// Reuses the email composer's wizard stepper styles
import '../email-composer/EmailBuilder.css'
import { codeText } from '../../../discounts/pickers/codeMessages'
import SmsCodeButton from '../../../discounts/pickers/SmsCodeButton'

/**
 * SMS Blast composer on the shared CreatorFrame: Message -> Audience ->
 * Review, wired to the real campaign API (the previous version was a
 * prototype with no API calls at all: sending showed a success screen
 * without sending, and Save draft discarded the message).
 */

const ink = '#1A1A1E'
const muted = '#8E8E93'
const line = '#ECECF0'

const SMSBlastComposerComponent = ({ onBack, onSave, dispatch, user, editCampaign, initialOfferCode }) => {
  // 0 = message, 1 = audience, 2 = review, 3 = success
  const [step, setStep] = useState(0)
  const [blastName, setBlastName] = useState(() => (initialOfferCode ? `${initialOfferCode.code} offer` : ''))
  // From a discount code's Share: the offer, the code, its conditions and
  // the link that applies it, ready to edit
  const [message, setMessage] = useState(() => (initialOfferCode ? codeText(initialOfferCode) : ''))
  const [isSending, setIsSending] = useState(false)
  // isSending is state, so two taps before React re-renders both saw false and
  // both created and sent a campaign; the ref is set synchronously
  const sendingRef = useRef(false)
  const [savingDraft, setSavingDraft] = useState(false)
  // When set, the composer is editing an existing draft/scheduled blast and
  // saves/sends UPDATE it in place rather than creating a new campaign.
  const [editingCampaignId, setEditingCampaignId] = useState(null)
  const [sendError, setSendError] = useState(null)
  const [isScheduled, setIsScheduled] = useState(false)
  // Multi-location (group owners only): other locations this also goes out
  // from, each as its own send there. Default: only here.
  const sendSites = useSendSites()
  const [otherSiteIds, setOtherSiteIds] = useState([])
  // { campaignId, sendNow, ids, results, error, retrying } once a send here has gone
  const [siteFan, setSiteFan] = useState(null)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  // Send confirmation happens in a popup on the Send click (an in-page
  // checkbox at the bottom of the review silently disabled the header
  // buttons, which read as broken, especially on mobile). Large sends get
  // the type-SEND gate instead, never both.
  const [showSendConfirm, setShowSendConfirm] = useState(false)
  // Mobile pins Send/Schedule to the bottom of the review step instead of
  // squeezing them into the top bar.
  const [isMobileView, setIsMobileView] = useState(typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  // Type-to-confirm for large sends: above this many texts (or beyond the
  // free allowance by a margin) the salon must type SEND, so a four-figure
  // bill can never be one accidental tap
  const LARGE_SEND_TEXT_THRESHOLD = 100
  const [showBigSendConfirm, setShowBigSendConfirm] = useState(false)
  const [bigSendText, setBigSendText] = useState('')
  const [afterBigSend, setAfterBigSend] = useState(null)
  const [showBizGate, setShowBizGate] = useState(false)
  const [bizDraft, setBizDraft] = useState(null)
  const [afterBizGate, setAfterBizGate] = useState(null)
  const textareaRef = useRef(null)

  // Audience (mirrors the email composer: one segment, hand-picked
  // clients, or both — no mode choice)
  const [audienceSegments, setAudienceSegments] = useState([])
  const [loadingAudience, setLoadingAudience] = useState(true)
  const [selectedSegments, setSelectedSegments] = useState([])
  const [selectedClients, setSelectedClients] = useState([])

  // Allowance + cost
  const [remainingSms, setRemainingSms] = useState(null)
  const [walletCredits, setWalletCredits] = useState(null)
  const [creditCosts, setCreditCosts] = useState(null)
  // The settings row as it came, for pounds: balancePence (or credits × 2p)
  // and what a text costs, straight from the API.
  const [walletRow, setWalletRow] = useState(null)
  const [autoRecharge, setAutoRecharge] = useState(null)
  const [showTopup, setShowTopup] = useState(false)
  const [costEstimate, setCostEstimate] = useState(null)

  const salonName = user?.userInfo?.hairdresser?.salon?.salonName || 'Your Salon'
  // PECR requires an opt-out route in every marketing text; Beu adds the line
  // when it's missing, so count and preview the text as it will go
  const hasOptOut = offersOptOut(message)
  const sending = withOptOut(message)
  const sms = analyseSms(sending)
  // Merge tags expand at send time — estimate the REAL per-client size so the
  // counter and cost quote don't flatter a template that bills as 2 texts.
  const est = analyseSmsExpanded(sending, salonName)
  const addOptOutLine = () => setMessage(m => withOptOut(m))

  // Business details cache refresh (the send gate reads it synchronously).
  useEffect(() => { hydrateBusinessDetails(localStorage.getItem('salonId')) }, [])

  // Every wizard step shares the CreatorFrame body as its scroller, so
  // without this a new step opened wherever the previous one was scrolled
  // to (e.g. Review starting at the bottom on mobile).
  useEffect(() => {
    const body = document.querySelector('.cf-body')
    if (body) body.scrollTop = 0
    window.scrollTo(0, 0)
  }, [step])

  // Continue editing an existing draft/scheduled blast: hydrate the message,
  // name and audience so a re-save UPDATES it instead of creating a copy.
  // Mirrors CampaignComposer's editCampaign effect (names in a hand-picked
  // audience re-hydrate when the client picker list loads).
  useEffect(() => {
    if (!editCampaign || !editCampaign.id) return
    setBlastName(editCampaign.name || '')
    setMessage(editCampaign.message || editCampaign._raw?.message || '')
    setEditingCampaignId(editCampaign.id)
    const seg = editCampaign._raw?.audienceSegment
    const pickedIds = editCampaign._raw?.audienceFilters?.clientIds
    if (seg === 'custom' && Array.isArray(pickedIds) && pickedIds.length) {
      setSelectedClients(pickedIds.map(id => ({ id: String(id), name: '' })))
    } else if (seg && seg !== 'custom') {
      setSelectedSegments([seg])
      // Segment + hand-picked union — restore the extras too
      if (Array.isArray(pickedIds) && pickedIds.length) {
        setSelectedClients(pickedIds.map(id => ({ id: String(id), name: '' })))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true
    marketingApiService.getAudienceSegments(dispatch)
      .then(segments => { if (alive) setAudienceSegments(segments || []) })
      .catch(() => { if (alive) setAudienceSegments([]) })
      .then(() => { if (alive) setLoadingAudience(false) })
    dispatch(marketingAction.getSettings())
      .then(res => {
        const r = res?.data?.responseData?.result
        if (alive && r) {
          if (r.remainingSms !== undefined && r.remainingSms !== null) setRemainingSms(r.remainingSms)
          if (r.walletCredits !== undefined) setWalletCredits(r.walletCredits)
          if (r.creditCosts) setCreditCosts(r.creditCosts)
          setWalletRow(r)
          if (r.autoRecharge) setAutoRecharge(r.autoRecharge)
        }
      })
      .catch(() => {})
    return () => { alive = false }
  }, [dispatch])


  // Cost estimate on review
  useEffect(() => {
    if (step !== 2) return undefined
    let alive = true
    marketingApiService.estimateCost(dispatch, {
      type: 'sms',
      // No segment means a hand-picked audience — 'custom' + clientIds
      audienceSegment: selectedSegments[0] || 'custom',
      clientIds: selectedClients.map(c => c.id),
      segments: est.segments,
      // The backend counts billable segments from the text itself
      message,
    })
      .then(res => { if (alive) setCostEstimate(res) })
      .catch(() => { if (alive) setCostEstimate(null) })
    return () => { alive = false }
  }, [step]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSegment = (id) => setSelectedSegments(prev => (prev[0] === id ? [] : [id]))

  const audienceSize = () => {
    // Segment total + hand-picked extras (overlap dedupes at send time)
    return selectedSegments.reduce((sum, id) => sum + (audienceSegments.find(s => s.id === id)?.count || 0), 0) + selectedClients.length
  }
  const audienceLabel = () => {
    const segment = audienceSegments.find(s => s.id === selectedSegments[0])
    if (!segment) {
      if (!selectedClients.length) return 'No one selected'
      return selectedClients.length === 1 ? selectedClients[0].name : `${selectedClients.length} hand-picked clients`
    }
    return selectedClients.length ? `${segment.name} + ${selectedClients.length} hand-picked` : segment.name
  }

  const insertTag = (tag) => {
    const el = textareaRef.current
    if (!el) { setMessage(m => m + tag); return }
    const start = el.selectionStart ?? message.length
    const end = el.selectionEnd ?? message.length
    const next = message.slice(0, start) + tag + message.slice(end)
    setMessage(next)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(start + tag.length, start + tag.length)
    })
  }

  const refreshBalance = () => {
    dispatch(marketingAction.getSettings())
      .then(res => {
        const r = res?.data?.responseData?.result
        if (r) {
          if (r.remainingSms != null) setRemainingSms(r.remainingSms)
          if (r.walletCredits !== undefined) setWalletCredits(r.walletCredits)
          if (r.creditCosts) setCreditCosts(r.creditCosts)
          setWalletRow(r)
          if (r.autoRecharge) setAutoRecharge(r.autoRecharge)
        }
      })
      .catch(() => {})
  }

  const campaignPayload = (extra = {}) => ({
    name: blastName || (CAMPAIGNS_VISIBLE ? 'Untitled text' : 'Untitled SMS blast'),
    type: 'sms',
    // Saved as it will go, opt-out line and all: the server adds it too, but a
    // server from before that change wouldn't, and the text must never go
    // without one (PECR). The server sees it's there and doesn't add another.
    message: sending,
    // No segment means a hand-picked audience — 'custom' + clientIds
    audienceSegment: selectedSegments[0] || 'custom',
    clientIds: selectedClients.map(c => c.id),
    ...extra,
  })

  // Update the existing draft when editing, otherwise create. Resolves to a
  // { _id } either way so the send flow can reference the campaign id uniformly.
  const persistCampaign = async (extra = {}) => {
    if (editingCampaignId) {
      await marketingApiService.updateCampaign(dispatch, { campaignId: editingCampaignId, ...campaignPayload(extra) })
      return { _id: editingCampaignId }
    }
    return marketingApiService.createCampaign(dispatch, campaignPayload(extra))
  }

  // After the send HERE has succeeded: the same text at the other chosen
  // locations, each sent or scheduled there under its own clients and
  // credits. Never throws — a failure there doesn't undo the send here; the
  // success screen says what happened and can try again (the server is
  // idempotent per location).
  const chosenOtherSites = () => (sendSites.show ? otherSiteIds.filter(id => sendSites.others.some(s => String(s.salonId) === String(id))) : [])
  const runSiteFan = async (fan) => {
    try {
      const results = await broadcastToSites(fan.campaignId, fan.ids, { sendNow: fan.sendNow })
      setSiteFan({ ...fan, results, error: null, retrying: false })
    } catch (e) {
      setSiteFan({ ...fan, results: [], error: (e && e.message) || 'We couldn’t reach your other locations.', retrying: false })
    }
  }
  const fanOutToSites = async (campaignId, sendNow) => {
    const ids = chosenOtherSites()
    if (!ids.length || !campaignId) return
    await runSiteFan({ campaignId, sendNow, ids })
  }

  const handleSend = async () => {
    if (sendingRef.current) return
    sendingRef.current = true
    setIsSending(true)
    setSendError(null)
    try {
      const created = await persistCampaign({ sendNow: true })
      if (created && created._id) {
        await marketingApiService.sendCampaign(dispatch, { campaignId: created._id, sendNow: true })
        await fanOutToSites(created._id, true)
      }
      setIsScheduled(false)
      setStep(3)
    } catch (e) {
      setSendError('Could not send the blast. Please try again.')
    } finally {
      sendingRef.current = false
      setIsSending(false)
    }
  }

  const handleSchedule = async (scheduled) => {
    setShowScheduleModal(false)
    if (sendingRef.current) return
    sendingRef.current = true
    setIsSending(true)
    setSendError(null)
    try {
      const created = await persistCampaign({
        sendNow: false,
        scheduledDate: scheduled.date,
        scheduledTime: scheduled.time,
      })
      if (created && created._id) {
        await marketingApiService.sendCampaign(dispatch, {
          campaignId: created._id,
          sendNow: false,
          scheduledDate: scheduled.date,
          scheduledTime: scheduled.time,
        })
        // Each copy is scheduled for the same date and time it carries from this one.
        await fanOutToSites(created._id, false)
      }
      setIsScheduled(true)
      setStep(3)
    } catch (e) {
      setSendError('Could not schedule the blast. Please try again.')
    } finally {
      sendingRef.current = false
      setIsSending(false)
    }
  }

  const handleSaveDraft = async () => {
    if (savingDraft) return
    setSavingDraft(true)
    setSendError(null)
    try {
      await persistCampaign({ sendNow: false })
      if (onSave) onSave()
    } catch (e) {
      setSendError('Could not save the draft. Please try again.')
    } finally {
      setSavingDraft(false)
    }
  }

  /* ── Success screen ── */
  if (step === 3) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 100001, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' }}>
        <div style={{ textAlign: 'center', maxWidth: 420, padding: 24 }}>
          <div style={{
            width: 86, height: 86, borderRadius: '50%', background: '#007EA0',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 26px'
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: ink, margin: '0 0 10px' }}>
            {isScheduled ? 'Blast scheduled' : 'Blast sent'}
          </h1>
          <p style={{ fontSize: 15, color: muted, margin: '0 0 28px' }}>
            Your message {isScheduled ? 'will be sent' : 'is on its way'} to {audienceSize().toLocaleString()} {audienceSize() === 1 ? 'client' : 'clients'}.
          </p>
          {siteFan && (
            <div style={{ margin: '-8px 0 26px', textAlign: 'left' }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 10px' }}>Your other locations</p>
              {siteFan.error ? (
                <>
                  <p role="alert" style={{ fontSize: 13.5, color: '#A05A1F', lineHeight: 1.5, margin: '0 0 10px' }}>{siteFan.error} Nothing was sent twice. Try again to finish the other locations.</p>
                  <button type="button" disabled={siteFan.retrying} onClick={() => { setSiteFan(f => ({ ...f, retrying: true })); runSiteFan(siteFan) }} style={{ padding: '9px 16px', borderRadius: 10, border: `1px solid ${line}`, background: '#fff', color: ink, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {siteFan.retrying ? 'Trying…' : 'Try again'}
                  </button>
                </>
              ) : (
                <SiteResultsList results={siteFan.results} sites={sendSites.others} />
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onSave || onBack}
            style={{ padding: '13px 26px', backgroundColor: '#29282D', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Back to campaigns
          </button>
        </div>
      </div>
    )
  }

  // Forced flow: business details must exist before anything sends
  const requireBusinessDetails = (proceed) => {
    if (isBusinessDetailsComplete(loadBusinessDetails(localStorage.getItem('salonId')))) { proceed(); return }
    setBizDraft(loadBusinessDetails(localStorage.getItem('salonId')) || { legalForm: '', registeredName: '', companyNumber: '', placeOfRegistration: 'England and Wales', registeredOffice: '', tradingName: '' })
    setAfterBizGate(() => proceed)
    setShowBizGate(true)
  }

  const totalTexts = audienceSize() * est.segments
  // The estimate knows the free allowance (same pool as the send's debit);
  // the local figure is only a fallback while it loads or if it fails
  const serverChargeable = costEstimate && typeof costEstimate.chargeable === 'number' ? costEstimate.chargeable : null
  const chargeableTexts = serverChargeable !== null ? serverChargeable
    : (remainingSms !== null ? Math.max(0, totalTexts - remainingSms) : 0)
  const isLargeSend = totalTexts > LARGE_SEND_TEXT_THRESHOLD || chargeableTexts > 100

  // The real charge (texts beyond the free allowance) when the estimate has
  // it; otherwise derive it from the every-text price and the allowance
  const rawEstimate = costEstimate && (costEstimate.cost !== undefined || costEstimate.totalCost !== undefined)
    ? Number(costEstimate.cost ?? costEstimate.totalCost)
    : null
  const estimatedCharge = costEstimate && typeof costEstimate.charge === 'number'
    ? costEstimate.charge
    : rawEstimate !== null && totalTexts > 0
      ? (remainingSms !== null ? (chargeableTexts / totalTexts) * rawEstimate : rawEstimate)
      : null

  const requireLargeSendConfirm = (proceed) => {
    if (!isLargeSend) { proceed(); return }
    setBigSendText('')
    setAfterBigSend(() => proceed)
    setShowBigSendConfirm(true)
  }

  // Send path: big sends confirm via the type-SEND gate, everything else via
  // a simple popup — one confirmation either way, never two.
  const startSend = () => {
    if (isLargeSend) requireLargeSendConfirm(handleSend)
    else setShowSendConfirm(true)
  }

  const stepPrimary =
    step === 0 ? { label: 'Continue to audience', onClick: () => setStep(1), disabled: !message.trim() || sms.overLimit } :
    step === 1 ? { label: 'Continue to review', onClick: () => setStep(2), disabled: selectedSegments.length === 0 && selectedClients.length === 0 } :
    // Review: on mobile the CTAs live in a pinned bottom bar instead
    step === 2 ? (isMobileView ? null : { label: isSending ? 'Sending…' : 'Send now', onClick: () => requireBusinessDetails(startSend), disabled: isSending }) :
    null
  const stepHeaderExtra = step === 2 ? (
    <button type="button" className="cf-btn-secondary" onClick={() => requireBusinessDetails(() => requireLargeSendConfirm(() => setShowScheduleModal(true)))} disabled={isSending} style={{ height: 40, padding: '0 18px' }}>Schedule</button>
  ) : null

  const previewText = previewSubstitute(sending, salonName) || 'Your message appears here as clients will see it.'

  return (
    <CreatorFrame
      name={blastName}
      onNameChange={setBlastName}
      namePlaceholder={CAMPAIGNS_VISIBLE ? 'Untitled text' : 'Untitled SMS blast'}
      onClose={onBack}
      onSave={handleSaveDraft}
      saveLabel="Save draft"
      saveVariant="secondary"
      saving={savingDraft}
      error={sendError}
      headerExtra={stepHeaderExtra}
      primaryAction={stepPrimary}
    >
      <div style={{ fontFamily: 'inherit', minHeight: '100%', backgroundColor: '#fafafa' }}>
        {/* Stepper — same chrome as the email composer */}
        <nav className="eb-stepper" aria-label="Blast steps">
          {[
            { id: 0, label: 'Message' },
            { id: 1, label: 'Audience' },
            { id: 2, label: 'Review' },
          ].map((s, i) => {
            const isCurrent = step === s.id
            const isDone = step > s.id
            return (
              <React.Fragment key={s.id}>
                {i > 0 && <span className={`eb-stepper-sep ${isDone || isCurrent ? 'is-done' : ''}`} aria-hidden />}
                <button
                  type="button"
                  className={`eb-stepper-item ${isCurrent ? 'is-current' : ''} ${isDone ? 'is-done' : ''}`}
                  onClick={() => { if (isDone) setStep(s.id) }}
                  disabled={!isDone && !isCurrent}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  <span className="eb-stepper-dot">
                    {isDone ? (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    ) : (i + 1)}
                  </span>
                  {s.label}
                </button>
              </React.Fragment>
            )
          })}
        </nav>

        {/* Step 0: Message — vertically centred while the content is
            shorter than the viewport (the wrapper grows past min-height
            when it is not, so nothing ever clips) */}
        {step === 0 && (
          <div style={{ minHeight: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div className="eb-sms-grid" style={{ width: '100%', maxWidth: 960, margin: '0 auto', padding: '32px clamp(14px, 4vw, 32px) 48px', boxSizing: 'border-box' }}>
            <div>
              <h2 style={{ fontSize: 24, fontWeight: 600, color: ink, margin: '0 0 8px' }}>Write your message</h2>
              <p style={{ fontSize: 14, color: muted, margin: '0 0 20px' }}>Short and personal works best. Tags fill in each client's details automatically.</p>

              {/* Idea chips fill the box with an editable starting point */}
              <p style={{ fontSize: 12, fontWeight: 600, color: muted, margin: '0 0 8px' }}>Need an idea?</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                {/* With discount codes on, a real code replaces the "mention this
                    text for 10% off" idea (a promise with no code behind it) */}
                {SMS_IDEAS.filter(idea => !(DISCOUNTS_VISIBLE && idea.id === 'sms-flash-offer')).map(idea => (
                  <button
                    key={idea.label}
                    type="button"
                    onClick={() => setMessage(idea.text)}
                    style={{
                      padding: '8px 13px', borderRadius: 100, border: `1px solid ${line}`,
                      background: '#fff', fontSize: 12.5, fontWeight: 600, color: '#4B4B52',
                      cursor: 'pointer', fontFamily: 'inherit'
                    }}
                  >
                    {idea.label}
                  </button>
                ))}
                <SmsCodeButton onInsert={(text) => insertTag(message.trim() ? ` ${text}` : text)} />
              </div>

              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={`Hi {{FirstName}}, we have a few openings this week at ${salonName}. Fancy a visit? Book here: {{BookingLink}}`}
                style={{
                  width: '100%', minHeight: 170, padding: '14px 16px', boxSizing: 'border-box',
                  border: `1px solid ${sms.overLimit ? '#B14438' : line}`, borderRadius: 14,
                  fontSize: 15, lineHeight: 1.55, fontFamily: 'inherit', outline: 'none', resize: 'vertical',
                  backgroundColor: '#fff'
                }}
              />

              {/* Counter */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '10px 2px 18px', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, color: sms.overLimit ? '#B14438' : muted, fontWeight: 600 }}>
                  {sms.length} characters · ≈{est.segments} {est.segments === 1 ? 'text' : 'texts'} per client after personalisation
                  {sms.overLimit && ` · over the ${MAX_SEGMENTS}-text limit`}
                </span>
              {est.segments > 1 && (
                <div style={{ marginTop: 6, background: '#FFF8E6', border: '1px solid #F5D982', borderRadius: 8, padding: '7px 10px', fontSize: 12, color: '#7A5B00', lineHeight: 1.5 }}>
                  Over one text once names and links are filled in, so each client is charged for {est.segments} texts ({formatUnitPence(est.segments * messagePrices(walletRow).textPence)} instead of {formatUnitPence(messagePrices(walletRow).textPence)}). Shorten the message to pay for one.
                </div>
              )}
                {!sms.gsm && message && (
                  <span style={{ fontSize: 12, color: '#A05A1F', fontWeight: 600 }}>
                    Emoji shrinks each text to {sms.single} characters
                  </span>
                )}
              </div>

              {message.trim() && !hasOptOut && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                  padding: '10px 14px', background: 'rgba(160,90,31,0.08)', borderRadius: 10, marginBottom: 18
                }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: '#A05A1F' }}>
                    UK rules need an opt-out in every marketing text. Beu adds this line at the end; add it yourself to put it somewhere else.
                  </span>
                  <button
                    type="button"
                    onClick={addOptOutLine}
                    style={{
                      border: '1px solid #A05A1F', background: '#fff', color: '#A05A1F',
                      fontSize: 12, fontWeight: 700, borderRadius: 100, padding: '6px 12px',
                      cursor: 'pointer', fontFamily: 'inherit'
                    }}
                  >
                    Add "Reply STOP to opt out"
                  </button>
                </div>
              )}

              {/* Merge tags */}
              <p style={{ fontSize: 12, fontWeight: 600, color: muted, margin: '0 0 8px' }}>Personalise</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {SMS_MERGE_TAGS.map(({ tag }) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => insertTag(tag)}
                    style={{
                      padding: '8px 13px', borderRadius: 100, border: `1px solid ${line}`,
                      background: '#fff', fontSize: 12.5, fontWeight: 600, color: '#4B4B52',
                      cursor: 'pointer', fontFamily: 'inherit'
                    }}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Live phone preview */}
            <div>
              <div style={{
                border: `1px solid ${line}`, borderRadius: 28, background: '#fff',
                padding: '22px 14px 26px', position: 'sticky', top: 24
              }} className="eb-sms-phone">
                <div style={{ width: 64, height: 5, borderRadius: 100, background: '#E5E5EA', margin: '0 auto 18px' }} />
                <p style={{ textAlign: 'center', fontSize: 11.5, fontWeight: 600, color: muted, margin: '0 0 14px' }}>{salonName}</p>
                <div style={{
                  background: '#007EA0', color: '#fff', padding: '11px 14px',
                  borderRadius: '16px 16px 4px 16px', fontSize: 13.5, lineHeight: 1.5,
                  marginLeft: 18, wordBreak: 'break-word', whiteSpace: 'pre-wrap'
                }}>
                  {previewText}
                </div>
                <p style={{ textAlign: 'center', fontSize: 10.5, color: '#C7C7CC', margin: '14px 0 0' }}>
                  Preview with example client details
                </p>
              </div>
            </div>
          </div>
          </div>
        )}

        {/* Step 1: Audience — one segment, hand-picked clients, or both
            (same combined layout as the email composer) */}
        {step === 1 && (
          <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px clamp(14px, 4vw, 32px) 140px' }}>
            <h2 style={{ fontSize: 24, fontWeight: 600, color: ink, margin: '0 0 8px' }}>Who should receive this?</h2>
            <p style={{ fontSize: 14, color: muted, margin: user?.userInfo?.hairdresser?.isSalonOwner === 1 ? '0 0 6px' : '0 0 28px' }}>Pick a smart segment, hand-pick individual clients, or combine the two.</p>
            {user?.userInfo?.hairdresser?.isSalonOwner === 1 && (
              <p style={{ fontSize: 12.5, color: muted, margin: '0 0 28px' }}>Counts include the own clients of self-employed stylists who have let you run their marketing.</p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {loadingAudience ? (
                [1, 2, 3, 4].map(i => <div key={i} style={{ height: 58, borderRadius: 12, backgroundColor: '#F2F2F7' }} />)
              ) : audienceSegments.length === 0 ? (
                <p style={{ fontSize: 13.5, color: muted, textAlign: 'center', padding: '32px 0' }}>Add clients to your salon to send blasts.</p>
              ) : (
                audienceSegments.map(segment => {
                  const isSelected = selectedSegments[0] === segment.id
                  return (
                    <button
                      key={segment.id}
                      type="button"
                      onClick={() => toggleSegment(segment.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 16px',
                        backgroundColor: '#fff', border: isSelected ? '2px solid #29282D' : `1px solid ${line}`,
                        borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit'
                      }}
                    >
                      <span style={{
                        width: 18, height: 18, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
                        border: isSelected ? '6px solid #29282D' : '2px solid #DEDEE2', backgroundColor: '#fff'
                      }} />
                      <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
                        <span style={{ fontSize: 14, fontWeight: 500, color: ink }}>{segment.name}</span>
                        {segment.description && <span style={{ fontSize: 12, color: muted }}>{segment.description}</span>}
                      </span>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: '#4B4B52', backgroundColor: '#F2F2F7', borderRadius: 100, padding: '4px 11px', flexShrink: 0 }}>
                        {(segment.count || 0).toLocaleString()}
                      </span>
                    </button>
                  )
                })
              )}
            </div>

            {/* Hand-picked clients: on top of a segment, or the whole audience */}
            <div style={{ marginTop: 28 }}>
              <p style={{ fontSize: 13.5, fontWeight: 600, color: ink, margin: '0 0 4px' }}>
                Add specific clients {selectedSegments.length > 0 && <span style={{ fontWeight: 400, color: muted }}>(optional)</span>}
              </p>
              <p style={{ fontSize: 12.5, color: muted, margin: '0 0 12px' }}>
                {selectedSegments.length > 0
                  ? 'Hand-pick clients to add on top of the segment, e.g. VIPs plus a few regulars.'
                  : 'Hand-pick exactly who receives it, no segment needed.'}
              </p>
              <ClientPicker dispatch={dispatch} channel="sms" selected={selectedClients} setSelected={setSelectedClients} />
            </div>
          </div>
        )}

        {/* Step 2: Review */}
        {step === 2 && (
          <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px clamp(14px, 4vw, 32px) 140px' }}>
            <h2 style={{ fontSize: 24, fontWeight: 600, color: ink, margin: '0 0 28px' }}>Review & send</h2>

            <div style={{ backgroundColor: '#fff', borderRadius: 14, border: `1px solid ${line}`, padding: 20, marginBottom: 16 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 12px' }}>Message</p>
              <div style={{
                background: '#007EA0', color: '#fff', padding: '11px 14px', borderRadius: '16px 16px 4px 16px',
                fontSize: 13.5, lineHeight: 1.5, maxWidth: 360, whiteSpace: 'pre-wrap', wordBreak: 'break-word'
              }}>
                {previewText}
              </div>
              <p style={{ fontSize: 12, color: muted, margin: '10px 0 0' }}>
                ≈{est.segments} {est.segments === 1 ? 'text' : 'texts'} per client
              </p>
              <p style={{
                fontSize: 12.5, fontWeight: 600, margin: '10px 0 0',
                color: hasOptOut ? '#007EA0' : '#A05A1F'
              }}>
                {hasOptOut
                  ? 'Opt-out included (UK marketing rules)'
                  : `Beu adds "${OPT_OUT_LINE}" at the end, as UK rules require. It’s in the count above.`}
              </p>
            </div>

            <div style={{ backgroundColor: '#fff', borderRadius: 14, border: `1px solid ${line}`, padding: 20, marginBottom: 16 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 12px' }}>Recipients</p>
              <p style={{ fontSize: 15, fontWeight: 600, color: ink, margin: 0 }}>
                {audienceLabel()} · {audienceSize().toLocaleString()} {audienceSize() === 1 ? 'client' : 'clients'}
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 14, paddingTop: 14, borderTop: '1px solid #F2F2F7' }}>
                <span style={{ fontSize: 13, color: '#4B4B52' }}>Texts to send</span>
                <strong style={{ fontSize: 13.5, color: ink }}>{(audienceSize() * sms.segments).toLocaleString()}</strong>
              </div>
              {remainingSms !== null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 10 }}>
                  <span style={{ fontSize: 13, color: '#4B4B52' }}>Free allowance remaining</span>
                  <strong style={{ fontSize: 13.5, color: (audienceSize() * sms.segments) > remainingSms ? '#A05A1F' : '#007EA0' }}>
                    {Number(remainingSms).toLocaleString()}
                  </strong>
                </div>
              )}
              {remainingSms !== null && (audienceSize() * sms.segments) > remainingSms && (
                <button
                  type="button"
                  onClick={() => setShowTopup(true)}
                  style={{ marginTop: 12, width: '100%', padding: 11, border: '1px solid #007EA0', borderRadius: 10, background: 'rgba(0,126,160,0.06)', color: '#007EA0', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
                >
                  More than your free allowance. Top up your balance
                </button>
              )}
              {estimatedCharge !== null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 10 }}>
                  <span style={{ fontSize: 13, color: '#4B4B52' }}>Estimated cost beyond allowance</span>
                  <strong style={{ fontSize: 13.5, color: estimatedCharge > 0 ? ink : '#007EA0' }}>
                    {estimatedCharge > 0 ? `£${estimatedCharge.toFixed(2)}` : 'Free'}
                  </strong>
                </div>
              )}
            </div>

            <SendLocationsCard value={otherSiteIds} onChange={setOtherSiteIds} channel="sms" disabled={isSending} />

            {(() => {
              // Texting at night damages goodwill; scheduling is already
              // constrained to daytime slots, so the nudge targets Send now
              const hour = new Date().getHours()
              if (hour < 21 && hour >= 8) return null
              return (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
                  background: 'rgba(160,90,31,0.07)', borderRadius: 12, marginBottom: 16
                }}>
                  <span style={{ fontSize: 15, lineHeight: 1 }}>🌙</span>
                  <span style={{ fontSize: 12.5, color: '#A05A1F', fontWeight: 600, lineHeight: 1.55 }}>
                    It is late for a text. Most clients prefer messages between 8am and 9pm, so consider using Schedule to pick a morning slot instead.
                  </span>
                </div>
              )
            })()}

          </div>
        )}

        {/* The final CTAs, pinned where the thumb is on mobile (the top-bar
            buttons were cramped there). */}
        {step === 2 && isMobileView && (
          <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20, display: 'flex', gap: 10, padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))', background: '#fff', borderTop: `1px solid ${line}` }}>
            <button
              type="button"
              className="cf-btn-secondary"
              onClick={() => requireBusinessDetails(() => requireLargeSendConfirm(() => setShowScheduleModal(true)))}
              disabled={isSending}
              style={{ flex: 1, height: 48, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              Schedule
            </button>
            <button
              type="button"
              className="cf-btn-primary"
              onClick={() => requireBusinessDetails(startSend)}
              disabled={isSending}
              style={{ flex: 1.4, height: 48, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isSending ? 'Sending…' : 'Send now'}
            </button>
          </div>
        )}
      </div>

      {/* Send confirmation for ordinary sends — the "cannot be undone" moment
          lives here, on the click, instead of as an in-page checkbox. */}
      {showSendConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100011, background: 'rgba(26,26,30,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowSendConfirm(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: '420px', background: '#fff', borderRadius: '16px', padding: '26px' }}>
            <h2 style={{ fontSize: '19px', fontWeight: 700, color: ink, margin: '0 0 6px' }}>Send this blast?</h2>
            <p style={{ fontSize: '14px', color: '#4B4B52', lineHeight: 1.6, margin: '0 0 20px' }}>
              It texts <strong>{audienceSize().toLocaleString()}</strong> {audienceSize() === 1 ? 'client' : 'clients'} straight away{chosenOtherSites().length ? ` (and from ${chosenOtherSites().length} other ${chosenOtherSites().length === 1 ? 'location' : 'locations'}, to their own clients)` : ''}, and sending cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setShowSendConfirm(false)}
                style={{ flex: 1, padding: '12px', border: `1px solid ${line}`, borderRadius: '10px', background: '#fff', fontSize: '14px', fontWeight: 600, color: ink, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setShowSendConfirm(false); handleSend() }}
                style={{ flex: 1.3, padding: '12px', border: 'none', borderRadius: '10px', background: '#29282D', fontSize: '14px', fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Send now
              </button>
            </div>
          </div>
        </div>
      )}

      {showBigSendConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100012, background: 'rgba(26,26,30,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowBigSendConfirm(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: '440px', background: '#fff', borderRadius: '16px', padding: '26px' }}>
            <h2 style={{ fontSize: '19px', fontWeight: 700, color: ink, margin: '0 0 6px' }}>This is a big send</h2>
            <p style={{ fontSize: '13.5px', color: muted, lineHeight: 1.55, margin: '0 0 16px' }}>
              You are about to send {totalTexts.toLocaleString()} texts to {audienceSize().toLocaleString()} {audienceSize() === 1 ? 'client' : 'clients'}.
            </p>

            <div style={{ background: '#F2F2F7', borderRadius: '12px', padding: '14px 16px', marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontSize: 13, color: '#4B4B52' }}>Texts to send</span>
                <strong style={{ fontSize: 13.5, color: ink }}>{totalTexts.toLocaleString()}</strong>
              </div>
              {remainingSms !== null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
                  <span style={{ fontSize: 13, color: '#4B4B52' }}>Beyond your free allowance</span>
                  <strong style={{ fontSize: 13.5, color: chargeableTexts > 0 ? '#A05A1F' : ink }}>{chargeableTexts.toLocaleString()}</strong>
                </div>
              )}
              {estimatedCharge !== null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
                  <span style={{ fontSize: 13, color: '#4B4B52' }}>Estimated cost</span>
                  <strong style={{ fontSize: 13.5, color: estimatedCharge > 0 ? ink : '#007EA0' }}>
                    {estimatedCharge > 0 ? `£${estimatedCharge.toFixed(2)}` : 'Covered by your free allowance'}
                  </strong>
                </div>
              )}
            </div>

            <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: '#4B4B52', margin: '0 0 6px' }}>
              Type SEND to confirm
            </label>
            <input
              type="text"
              value={bigSendText}
              onChange={(e) => setBigSendText(e.target.value)}
              placeholder="SEND"
              autoFocus
              style={{
                width: '100%', height: '46px', padding: '0 16px', boxSizing: 'border-box',
                border: `1px solid ${bigSendText.trim().toUpperCase() === 'SEND' ? '#007EA0' : line}`,
                borderRadius: '10px', fontSize: '15px', fontWeight: 700, letterSpacing: '0.08em',
                fontFamily: 'inherit', outline: 'none', textTransform: 'uppercase'
              }}
            />

            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
              <button
                type="button"
                onClick={() => setShowBigSendConfirm(false)}
                style={{ flex: 1, padding: '12px', border: `1px solid ${line}`, borderRadius: '10px', background: '#fff', fontSize: '14px', fontWeight: 600, color: ink, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={bigSendText.trim().toUpperCase() !== 'SEND'}
                onClick={() => {
                  setShowBigSendConfirm(false)
                  if (afterBigSend) afterBigSend()
                }}
                style={{
                  flex: 1.3, padding: '12px', border: 'none', borderRadius: '10px',
                  background: bigSendText.trim().toUpperCase() === 'SEND' ? '#29282D' : '#C7C7CC',
                  fontSize: '14px', fontWeight: 600, color: '#fff',
                  cursor: bigSendText.trim().toUpperCase() === 'SEND' ? 'pointer' : 'default', fontFamily: 'inherit'
                }}
              >
                Confirm send
              </button>
            </div>
          </div>
        </div>
      )}

      {showBizGate && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100010, background: 'rgba(26,26,30,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowBizGate(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: '16px', padding: '28px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: ink, margin: '0 0 6px' }}>Confirm your business details first</h2>
            <p style={{ fontSize: '13.5px', color: muted, lineHeight: 1.55, margin: '0 0 20px' }}>
              UK law requires marketing messages to identify the sender. Tell us how your business is set up and it is added for you. You only do this once.
            </p>
            <BusinessDetailsForm
              value={bizDraft}
              onChange={(field, value) => setBizDraft(prev => ({ ...prev, [field]: value }))}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
              <button type="button" onClick={() => setShowBizGate(false)} style={{ flex: 1, padding: '12px', border: `1px solid ${line}`, borderRadius: '10px', background: '#fff', fontSize: '14px', fontWeight: 600, color: ink, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button
                type="button"
                disabled={!isBusinessDetailsComplete(bizDraft)}
                onClick={() => {
                  saveBusinessDetails(localStorage.getItem('salonId'), bizDraft)
                  setShowBizGate(false)
                  if (afterBizGate) afterBizGate()
                }}
                style={{
                  flex: 1.4, padding: '12px', border: 'none', borderRadius: '10px',
                  background: isBusinessDetailsComplete(bizDraft) ? '#29282D' : '#C7C7CC',
                  fontSize: '14px', fontWeight: 600, color: '#fff',
                  cursor: isBusinessDetailsComplete(bizDraft) ? 'pointer' : 'default', fontFamily: 'inherit'
                }}
              >
                Save and continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showTopup && (
        <SmsWalletTopupModal
          balancePence={walletBalancePence(walletRow)}
          currentBalance={walletCredits}
          prices={messagePrices(walletRow)}
          creditCosts={creditCosts}
          autoRecharge={autoRecharge}
          onSuccess={refreshBalance}
          onClose={() => setShowTopup(false)}
        />
      )}
      {showScheduleModal && (
        <ScheduleModal
          recipientCount={audienceSize()}
          onSchedule={handleSchedule}
          onClose={() => setShowScheduleModal(false)}
        />
      )}
    </CreatorFrame>
  )
}

const mapStateToProps = (state) => ({ user: state.user })
export const SMSBlastComposer = connect(mapStateToProps)(SMSBlastComposerComponent)
export default SMSBlastComposer
